<template>
  <div class="home-view">

    <!-- ── Hero ──────────────────────────────────────────── -->
    <section class="hero">
      <div class="hero-photo"></div>
      <div class="hero-vignette"></div>
      <div class="container hero-inner">
        <div class="hero-panel">
          <p class="hero-kicker">
            {{ t('Chinese Lutherans in Mission Building', '美國路德會華人聖工聯會', '美国路德会华人圣工联会') }}
          </p>
          <h1>{{ getText(content().home, 'welcomeTitle') }}</h1>
          <p class="hero-tagline">{{ getText(content().home, 'welcomeText') }}</p>
          <div class="hero-cta">
            <router-link to="/about" class="btn btn-hero-solid">
              {{ t('About CLIMB', '關於我們', '关于我们') }}
            </router-link>
            <router-link to="/events" class="btn btn-ghost">
              {{ t('Upcoming Conference', '即將舉行的會議', '即将举行的会议') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Seasonal messages ─────────────────────────────── -->
    <section v-if="content().home.seasonalMessages?.length" class="container">
      <div
        v-for="(msg, i) in content().home.seasonalMessages"
        :key="i"
        class="seasonal-message"
      >
        <h2>{{ getText(msg, 'title') || (currentLanguage === 'en' ? msg.english?.[0] : msg.chinese?.[0]) }}</h2>
        <p>{{ getText(msg, 'text')  || (currentLanguage === 'en' ? msg.english?.[1] : msg.chinese?.[1]) }}</p>
      </div>
    </section>

    <!-- ── Mission strip ─────────────────────────────────── -->
    <section class="mission-strip">
      <div class="container mission-inner">
        <p class="mission-label">
          {{ t('Our Purpose', '我們的宗旨', '我们的宗旨') }}
        </p>
        <blockquote class="mission-quote">
          {{ t(
            'CLIMB exists to foster vision, support, and collaboration for Chinese Lutheran churches and missions through faithful biblical and Lutheran teaching.',
            '聖工聯會致力於藉著忠實的聖經和路德宗教導，培育華人路德宗教會和宣教使命的異象、支持與合作。',
            '圣工联会致力于藉着忠实的圣经和路德宗教导，培育华人路德宗教会和宣教使命的异象、支持与合作。'
          ) }}
        </blockquote>
      </div>
    </section>

    <!-- ── Explore sections ─────────────────────────────── -->
    <section class="explore-section container">
      <h2 class="section-title text-center">
        {{ t('Explore CLIMB', '探索聖工聯會', '探索圣工联会') }}
      </h2>
      <div class="explore-grid">

        <router-link to="/gospel" class="explore-tile tile-gospel">
          <div class="tile-inner">
            <span class="tile-icon">✝</span>
            <h3>{{ t('Gospel & Faith', '福音與信仰', '福音与信仰') }}</h3>
            <p>{{ t('Who is Jesus? What do Lutherans believe?', '耶穌是誰？路德宗信徒相信什麼？', '耶稣是谁？路德宗信徒相信什么？') }}</p>
            <span class="tile-arrow">→</span>
          </div>
        </router-link>

        <router-link to="/fellowship" class="explore-tile tile-fellowship">
          <div class="tile-inner">
            <span class="tile-icon">⛪</span>
            <h3>{{ t('Fellowship', '聯會信息', '联会信息') }}</h3>
            <p>{{ t('Pastoral directory and member churches', '牧師名錄與會員教會', '牧师名录与会员教会') }}</p>
            <span class="tile-arrow">→</span>
          </div>
        </router-link>

        <router-link to="/resources" class="explore-tile tile-resources">
          <div class="tile-inner">
            <span class="tile-icon">📖</span>
            <h3>{{ t('Resources', '資源', '资源') }}</h3>
            <p>{{ t('Worship materials, booklets, and audio', '崇拜材料、小冊子和音頻資源', '崇拜材料、小册子和音频资源') }}</p>
            <span class="tile-arrow">→</span>
          </div>
        </router-link>

        <router-link to="/events" class="explore-tile tile-events">
          <div class="tile-inner">
            <span class="tile-icon">📅</span>
            <h3>{{ t('Events', '活動', '活动') }}</h3>
            <p>{{ t('Annual conference and gatherings', '年度會議及集會', '年度会议及集会') }}</p>
            <span class="tile-arrow">→</span>
          </div>
        </router-link>

      </div>
    </section>

    <!-- ── Upcoming events ─────────────────────────────── -->
    <section class="events-section" v-if="upcomingEvents.length">
      <div class="container">
        <h2 class="section-title">
          {{ t('Upcoming Events', '即將舉行的活動', '即将举行的活动') }}
        </h2>
        <div class="events-list">
          <router-link
            v-for="event in upcomingEvents.slice(0, 3)"
            :key="event.slug"
            :to="`/events/${event.slug}`"
            class="event-item"
          >
            <div class="event-date">{{ formatDate(event.date) }}</div>
            <div class="event-title">{{ getText(event, 'title') }}</div>
            <p v-if="getText(event, 'subtitle')" class="event-subtitle">
              {{ getText(event, 'subtitle') }}
            </p>
          </router-link>
        </div>
        <div class="text-center mt-2">
          <router-link to="/events" class="btn btn-outline">
            {{ t('View All Events', '查看所有活動', '查看所有活动') }}
          </router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { languageMixin } from '../mixins/languageMixin'

export default {
  name: 'HomeView',
  mixins: [languageMixin],
  inject: ['content'],
  data() {
    return { allEvents: [] }
  },
  async mounted() {
    try {
      const res  = await fetch('/events.json')
      const data = await res.json()
      this.allEvents = data.events || []
    } catch (e) {
      console.error('Error loading events:', e)
    }
  },
  computed: {
    upcomingEvents() {
      return this.allEvents
        .filter(e => e.status === 'upcoming')
        .sort((a, b) => new Date(a.date) - new Date(b.date))
    }
  },
  methods: {
    formatDate(str) {
      if (!str) return ''
      const d = new Date(str)
      const opts = { year: 'numeric', month: 'long', day: 'numeric' }
      if (this.currentLanguage === 'zh-Hant') return d.toLocaleDateString('zh-TW', opts)
      if (this.currentLanguage === 'zh-Hans') return d.toLocaleDateString('zh-CN', opts)
      return d.toLocaleDateString('en-US', opts)
    }
  }
}
</script>

<style scoped>
/* ── Hero ───────────────────────────────────────────────── */
.hero {
  position: relative;
  min-height: 580px;
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-bottom: 0;
}

.hero-photo {
  position: absolute;
  inset: 0;
  background: url('@/assets/images/climb-hero.png') center / cover;
  z-index: 0;
}

/* Left-heavy gradient — shows the photo on the right */
.hero-vignette {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    105deg,
    rgba(20, 26, 42, 0.97) 0%,
    rgba(20, 26, 42, 0.92) 38%,
    rgba(20, 26, 42, 0.55) 62%,
    rgba(20, 26, 42, 0.15) 80%,
    rgba(20, 26, 42, 0.0) 100%
  );
}

.hero-inner {
  position: relative;
  z-index: 2;
  padding-top: 5rem;
  padding-bottom: 5rem;
}

.hero-panel {
  max-width: 560px;
}

.hero-kicker {
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: var(--color-primary-light);
  margin-bottom: 1rem;
}

.hero-panel h1 {
  font-family: var(--font-family-heading);
  font-size: 2.9rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  margin-bottom: 1.1rem;
  letter-spacing: -.02em;
}

.hero-tagline {
  font-size: 1.1rem;
  color: rgba(255,255,255,.78);
  line-height: 1.7;
  margin-bottom: 2rem;
  font-weight: 400;
}

.hero-cta {
  display: flex;
  gap: .85rem;
  flex-wrap: wrap;
}

.btn-hero-solid {
  background: var(--color-primary);
  color: white;
  border: 2px solid transparent;
}
.btn-hero-solid:hover {
  background: var(--color-primary-dark);
  color: white;
}

/* ── Mission strip ──────────────────────────────────────── */
.mission-strip {
  background: var(--color-text);
  padding: 2.5rem 0;
}

.mission-inner {
  display: flex;
  align-items: baseline;
  gap: 2rem;
}

.mission-label {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: rgba(255,255,255,.45);
  white-space: nowrap;
  flex-shrink: 0;
  margin: 0;
}

.mission-quote {
  font-family: var(--font-family-heading);
  font-size: 1.05rem;
  font-style: italic;
  color: rgba(255,255,255,.82);
  line-height: 1.7;
  border-left: 3px solid var(--color-primary-light);
  padding-left: 1.25rem;
  margin: 0;
}

/* ── Explore grid ───────────────────────────────────────── */
.explore-section {
  padding-top: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
}

.explore-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

.explore-tile {
  display: block;
  text-decoration: none;
  color: inherit;
  background: var(--color-background-card);
  transition: background .18s;
}

.explore-tile:hover { background: var(--color-background-alt); }
.explore-tile:hover .tile-arrow { transform: translateX(4px); }

.tile-inner {
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tile-icon {
  font-size: 1.5rem;
  margin-bottom: .85rem;
  line-height: 1;
}

.tile-gospel     .tile-icon { color: var(--color-gospel); }
.tile-fellowship .tile-icon { color: var(--color-fellowship); }
.tile-resources  .tile-icon { color: var(--color-publications); }
.tile-events     .tile-icon { color: var(--color-primary); }

.explore-tile h3 {
  font-family: var(--font-family-heading);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: .4rem;
}

.explore-tile p {
  font-size: .88rem;
  color: var(--color-text-light);
  line-height: 1.55;
  margin: 0 0 auto;
  flex: 1;
}

.tile-arrow {
  display: inline-block;
  font-size: .9rem;
  color: var(--color-text-muted);
  margin-top: 1rem;
  transition: transform .2s ease;
}

/* Gospel tile top border accent */
.tile-gospel     { border-top: 3px solid var(--color-gospel); }
.tile-fellowship { border-top: 3px solid var(--color-fellowship); }
.tile-resources  { border-top: 3px solid var(--color-publications); }
.tile-events     { border-top: 3px solid var(--color-primary); }

/* ── Events section ─────────────────────────────────────── */
.events-section {
  background: var(--color-background-alt);
  padding: var(--spacing-xl) 0;
}

.events-list {
  max-width: 720px;
  margin: 0 auto var(--spacing-lg);
}

/* Responsive */
@media (max-width: 900px) {
  .explore-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 640px) {
  .hero-panel h1 { font-size: 2rem; }
  .hero-inner { padding-top: 3.5rem; padding-bottom: 3.5rem; }
  .hero { min-height: 460px; }
  .explore-grid { grid-template-columns: 1fr; }
  .mission-inner { flex-direction: column; gap: .75rem; }
}
</style>
